import { Divider, FormGroup, FormParagraph } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { CustomControlMode, ServiceCode } from '@common/enums';
import { ProcessEntity } from '@common/enums/processEntity';
import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import type { IOrthOProcedureCustomControlData } from '../../../../../common/interfaces/ophthalmology/orthO/orthOProcedureCustomControlData';
import type { ICustomControlProcedureAddEditProps } from '../interfaces/customControlProcedureAddEditProps';
import { ToastType } from '../../../../../common/enums/toastType';
import { OrthOVerwijzer } from '../../../../../common/validation/customControls/ophthalmology';
import { translateKey } from '../../../../translate';
import { showServiceToast } from '../../../../utils/showToast';
import { SaveButton } from '../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer } from '../../../elements/common';
import { FormContextProvider } from '../../../elements/formContext';
import { SearchableEnumDropdown } from '../../../elements/SearchableEnumDropdown';
import { InputTextField } from '../../../userDataManagement/InputText';
import { PatientInsuranceOldCarepath } from '../../episodeCategory/customControls/patientInsurance/PatientInsuranceOldCarepath';
import { CareProviderSelector } from '../utils/v1/CareProviderSelector';
import { ServiceOrganizationConsumerSelector } from '../utils/v1/ServiceOrganizationConsumerSelector';
import { OrthOExaminationOrder } from './orthOExaminationOrder';
import { OrthOV1ProcedureAnamnese } from './OrthOV1ProcedureAnamnese';
import { OrthOV1ProcedureExaminations } from './OrthOV1ProcedureExaminations';
import { OrthOV1ProcedurePhotos } from './OrthOV1ProcedurePhotos';

interface IOrthopticsV1ProcedureAddEditProps extends ICustomControlProcedureAddEditProps {
    isOrthOFol?: boolean;
}

type IOrthOCylindscrishFields = Pick<
    IOrthOProcedureCustomControlData,
    | 'visus_cc_refractie_cylindrisch_od'
    | 'visus_cc_refractie_cylindrisch_os'
    | 'visus_bcva_refractie_cylindrisch_od'
    | 'visus_bcva_refractie_cylindrisch_os'
    | 'visus_ec_refractie_cylindrisch_od'
    | 'visus_ec_refractie_cylindrisch_os'
    | 'refractie_cylindrisch_od'
    | 'refractie_cylindrisch_os'
    | 'autorefractie_cylindrisch_od'
    | 'autorefractie_cylindrisch_os'
    | 'autorefractie_cyclo_cylindrisch_od'
    | 'autorefractie_cyclo_cylindrisch_os'
    | 'skiascopie_cyclo_cylindrisch_od'
    | 'skiascopie_cyclo_cylindrisch_os'
>;

export const orthOCylindrishFields: (keyof IOrthOCylindscrishFields)[] = [
    'visus_cc_refractie_cylindrisch_od',
    'visus_cc_refractie_cylindrisch_os',
    'visus_bcva_refractie_cylindrisch_od',
    'visus_bcva_refractie_cylindrisch_os',
    'visus_ec_refractie_cylindrisch_od',
    'visus_ec_refractie_cylindrisch_os',
    'refractie_cylindrisch_od',
    'refractie_cylindrisch_os',
    'autorefractie_cylindrisch_od',
    'autorefractie_cylindrisch_os',
    'autorefractie_cyclo_cylindrisch_od',
    'autorefractie_cyclo_cylindrisch_os',
    'skiascopie_cyclo_cylindrisch_od',
    'skiascopie_cyclo_cylindrisch_os',
];

export function OrthopticsV1ProcedureAddEdit(props: IOrthopticsV1ProcedureAddEditProps) {
    const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false);
    const [buttons, setButtons] = useState<JSX.Element[]>([]);

    useEffect(() => {
        const buttonList = [
            <CancelButton
                cancel={props.onCancel}
                entity={ProcessEntity.procedure}
                isExisting={!!props.request.id}
                key="cancel-button"
                revertChanges={props.revertProcedureChanges}
                serviceCode={props.service.code}
                serviceType={props.service.serviceType}
            />,
            <SaveButton
                buttonDisabled={isSaveButtonDisabled}
                key="save-button"
                save={async () => {
                    await saveProcedure('draft');
                }}
            />,
        ];

        if (!props.service || !props.service.isStandaloneGraderNotAllowed) {
            buttonList.push(
                <SaveSendButton
                    buttonDisabled={isSaveButtonDisabled}
                    saveSend={async () => {
                        await saveProcedure('final');
                    }}
                />
            );
        }

        setButtons(buttonList);
    }, []);

    return (
        <>
            <FormContextProvider
                value={{
                    errors: props.errors,
                    item: props.procedure.customControlData,
                    onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                    onDoValidate: async (key) => await props.validateOneCustomControl(key),
                    placeholderGroupName: 'procedure',
                }}
            >
                <PatientInsuranceOldCarepath
                    handleChangeCustomControl={props.handleChangeCustomControl}
                    isPatientInformed={props.procedure.customControlData.isPatientInformed}
                    mode={CustomControlMode.edit}
                    onCancel={props.onCancel}
                    serviceCode={props.isOrthOFol ? ServiceCode.OrthOFol : ServiceCode.OrthO}
                />
                {!props.isOrthOFol && props.request.isDirectProcedureAdded && (
                    <FormGroup
                        hasErrors={props.errors.has('customControlData.verwijzer')}
                        isRequired
                        label="Verwijzer"
                    >
                        <SearchableEnumDropdown
                            enumTranslationName="orthOVerwijzer"
                            error={props.errors.get('customControlData.verwijzer')}
                            jsEnum={OrthOVerwijzer}
                            onChange={async (value) => {
                                if (
                                    value !== OrthOVerwijzer.other &&
                                    props.procedure.customControlData.verwijzer_overig
                                ) {
                                    await props.handleChangeCustomControl({ verwijzer_overig: null });
                                }
                                await props.handleChangeCustomControl({ verwijzer: value });
                            }}
                            onDoValidate={async () => {
                                await props.validateOneCustomControl('verwijzer');
                            }}
                            placeholder="Kies uit de lijst"
                            value={props.procedure.customControlData.verwijzer}
                        />
                    </FormGroup>
                )}

                {!props.isOrthOFol && !props.request.isDirectProcedureAdded && (
                    <FormGroup label="Verwijzer">
                        <FormParagraph>
                            {translateKey(`orthOVerwijzerEnum.${OrthOVerwijzer.generalPractitioner}`)}
                        </FormParagraph>
                    </FormGroup>
                )}

                {props.procedure.customControlData.verwijzer === OrthOVerwijzer.other && (
                    <InputTextField
                        error={props.errors.get('customControlData.verwijzer_overig')}
                        fieldKey="verwijzer_overig"
                        hasErrors={props.errors.has('customControlData.verwijzer_overig')}
                        isRequired={true}
                        label="orthOProcedure.referrer_other"
                        placeholder="Overig verwijzer"
                        tabIndex={OrthOExaminationOrder.previousExamination}
                        value={props.procedure.customControlData.verwijzer_overig}
                    />
                )}

                <ServiceOrganizationConsumerSelector {...props} />
                <CareProviderSelector
                    {...props}
                    entity={props.procedure}
                />

                <Divider
                    color="minimal"
                    spacing="medium"
                />
                <OrthOV1ProcedureAnamnese
                    customControlData={props.procedure.customControlData}
                    errors={props.errors}
                    isOrthOFol={props.isOrthOFol}
                    validateOneCustomControl={props.validateOneCustomControl}
                />

                <Divider
                    color="minimal"
                    spacing="medium"
                />
                <OrthOV1ProcedureExaminations
                    customControlData={props.procedure.customControlData}
                    errors={props.errors}
                    handleChangeCustomControl={props.handleChangeCustomControl}
                    isOrthOFol={props.isOrthOFol}
                    isReadOnly={false}
                    validateOneCustomControl={props.validateOneCustomControl}
                />

                <Divider
                    color="minimal"
                    spacing="medium"
                />
                <OrthOV1ProcedurePhotos
                    {...props}
                    isOrthOFol={props.isOrthOFol}
                    isReadOnly={false}
                    setIsSaveButtonDisabled={setIsSaveButtonDisabled}
                    title="Foto's"
                />
            </FormContextProvider>

            <ButtonContainer
                alignRight
                buttons={buttons}
                hideButtons={props.isBioGrader}
            />
        </>
    );
    async function saveProcedure(saveType: 'draft' | 'final') {
        setIsSaveButtonDisabled(true);

        const updatedCylindrishValues = addMinusCylindrishValues(props.procedure.customControlData);
        await props.handleChangeCustomControl(updatedCylindrishValues);

        const validatedProcedure = await props.validateProcedure(saveType);

        if (!validatedProcedure) {
            const newCylindrishValues = removeMinusCylindrishValues(props.procedure.customControlData);
            await props.handleChangeCustomControl(newCylindrishValues);
            setIsSaveButtonDisabled(false);

            return;
        }

        const requestId = await props.onSaveProcedure(saveType);
        setIsSaveButtonDisabled(false);
        if (!requestId) {
            return;
        }

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.procedureSubmitted',
                props.service,
                'procedure',
                translateKey('toast.submitDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'procedure',
                translateKey('toast.draftDefaultMessage')
            );
        }

        await props.onSave(requestId);
    }
}

export function addMinusCylindrishValues(customControlData: IOrthOProcedureCustomControlData) {
    const updatedCylindrishValues: Partial<IOrthOProcedureCustomControlData> = {};

    for (const key of orthOCylindrishFields) {
        const value = customControlData[key];
        if (value && !value.startsWith('-')) {
            updatedCylindrishValues[key] = `-${value}`;
        }
    }

    return updatedCylindrishValues;
}

export function removeMinusCylindrishValues(customControlData: IOrthOProcedureCustomControlData) {
    const updatedCylindrishValues: Partial<IOrthOProcedureCustomControlData> = {};

    for (const key of orthOCylindrishFields) {
        const value = customControlData[key];
        if (value && value.startsWith('-')) {
            updatedCylindrishValues[key] = value.replace(/\-+/, '');
        }
    }

    return updatedCylindrishValues;
}
